import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { brand, navLinks, tickerItems } from "@/mocks/site";

const trendStyle: Record<string, string> = {
  反转: "text-accent-300",
  升温: "text-accent-400",
  回落: "text-primary-300",
  待定: "text-background-300",
};

export default function SiteHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* 风向速览条 */}
      <div className="w-full bg-primary-950 text-background-100">
        <div className="flex h-9 w-full items-center gap-3 px-4 md:px-6 lg:px-10">
          <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-[11px] font-medium tracking-wider text-accent-300">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
            实时风向
          </span>
          <div className="relative flex-1 overflow-hidden hide-scrollbar">
            <div className="marquee-track animate-marquee-x">
              {[...tickerItems, ...tickerItems].map((item, index) => (
                <span
                  key={`${item.label}-${index}`}
                  className="flex items-center gap-2 pr-8 text-[11px] text-background-200"
                >
                  <span className={`font-medium ${trendStyle[item.trend] ?? "text-background-300"}`}>
                    [{item.trend}]
                  </span>
                  <span className="text-background-100">{item.label}</span>
                  <span className="text-background-400">{item.note}</span>
                </span>
              ))}
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-[11px] text-background-400 md:flex">
            已归档 1,284 起
          </span>
        </div>
      </div>

      {/* 报头 */}
      <div className="w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex h-[68px] w-full items-center justify-between gap-6 px-4 md:px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-3" aria-label="瓜田黑料首页">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-800 text-background-50">
              <i className="ri-plant-line text-xl" aria-hidden="true" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
                {brand.name}
              </span>
              <span className="mt-1 text-[10px] font-normal tracking-[0.28em] text-foreground-500">
                {brand.suffix}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  [
                    "relative whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors",
                    isActive
                      ? "font-medium text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950",
                  ].join(" ")
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <Link
              to="/cases"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-archive-line text-base" aria-hidden="true" />
              翻案例库
            </Link>
            <Link
              to="/guide"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
              核验方法
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-label={open ? "关闭菜单" : "打开菜单"}
            aria-expanded={open}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {open && (
        <div className="w-full border-b border-background-200 bg-background-50 md:hidden">
          <nav className="px-4 py-4 md:px-6 lg:px-10" aria-label="移动端主导航">
            <ul className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      [
                        "flex items-center justify-between rounded-md px-4 py-3 text-sm",
                        isActive
                          ? "bg-primary-50 font-medium text-primary-700"
                          : "text-foreground-800 hover:bg-background-100",
                      ].join(" ")
                    }
                  >
                    {link.label}
                    <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-col gap-2 border-t border-background-200 pt-4">
              <Link
                to="/cases"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 rounded-md bg-primary-800 px-4 py-3 text-sm font-medium text-background-50"
              >
                <i className="ri-archive-line text-base" aria-hidden="true" />
                翻案例库
              </Link>
              <Link
                to="/guide"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-3 text-sm text-foreground-700"
              >
                <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
                核验方法
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}